import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileDown, CheckCircle2, Loader2, Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChecklistExportModal({ open, onClose, content, sessionId, defaultTitle }) {
  const [form, setForm] = useState({ full_name: "", email: "", title: defaultTitle || "Your Fertility Appointment Prep" });
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ full_name: "", email: "", title: defaultTitle || "Your Fertility Appointment Prep" });
      setStatus("idle");
      setErr("");
    }
  }, [open, defaultTitle]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErr("");
    try {
      await axios.post(`${API}/chat/export-checklist`, {
        session_id: sessionId,
        full_name: form.full_name,
        email: form.email,
        content,
        title: form.title,
      });
      setStatus("success");
    } catch (e2) {
      setStatus("error");
      setErr(e2?.response?.data?.detail || "Could not send. Please try again.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="checklist-export-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-[#3F5043]/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#F8F7F3] rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
          >
            <div className="bg-[#3F5043] px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3 text-[#F8F7F3]">
                <div className="w-10 h-10 rounded-full bg-[#AE8F78] flex items-center justify-center">
                  <FileDown size={16} />
                </div>
                <div>
                  <div className="font-serif italic text-xl leading-tight">Email me this checklist</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#F8F7F3]/70 font-semibold">
                    Printable PDF
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="checklist-close"
                className="w-9 h-9 rounded-full bg-[#F8F7F3]/10 text-[#F8F7F3] flex items-center justify-center hover:bg-[#F8F7F3]/20"
              >
                <X size={14} />
              </button>
            </div>

            {status === "success" ? (
              <div data-testid="checklist-success" className="p-8 text-center">
                <CheckCircle2 size={44} strokeWidth={1.2} className="text-[#899480] mx-auto mb-4" />
                <div className="font-serif text-2xl text-[#3F5043] mb-2">Check your inbox.</div>
                <p className="text-sm text-[#3F5043]/70 max-w-xs mx-auto">
                  We've emailed <strong>{form.email}</strong> with a secure link to download your checklist PDF.
                </p>
                <button
                  onClick={onClose}
                  className="mt-6 text-xs uppercase tracking-[0.22em] text-[#899480] hover:text-[#3F5043]"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 space-y-4" data-testid="checklist-form">
                <p className="text-sm text-[#3F5043]/80 leading-relaxed">
                  We'll email you a beautifully formatted PDF of your prep checklist — take it to your appointment or share with your partner.
                </p>
                <input
                  data-testid="checklist-name"
                  required
                  placeholder="Full name"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full bg-[#EDEDEE] rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480]"
                />
                <input
                  data-testid="checklist-email"
                  required
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-[#EDEDEE] rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480]"
                />
                {err && (
                  <div data-testid="checklist-error" className="text-xs text-[#875F45] bg-[#875F45]/10 border border-[#875F45]/30 rounded-xl px-3 py-2">
                    {err}
                  </div>
                )}
                <button
                  type="submit"
                  data-testid="checklist-submit"
                  disabled={status === "loading"}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <><Loader2 size={14} className="animate-spin" /> Sending…</>
                  ) : (
                    <><Mail size={14} /> Email my checklist</>
                  )}
                </button>
                <p className="text-[11px] text-[#3F5043]/55 text-center">
                  Educational only — not medical advice. We'll never share your email.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
